import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import { ReportProblemOutlined as WarnIcon } from '@mui/icons-material';

/**
 * App-level error boundary.
 *
 * Without this, any render-time exception (e.g. a heavy graph view throwing
 * mid-transition while sigma tears down/rebuilds) unmounts the whole React tree
 * and the user sees a blank white screen with no way out. This catches it and
 * shows a recoverable fallback instead.
 *
 * `resetKey` (pass the current pathname) clears the error on navigation, so
 * moving to another tab recovers without a hard reload.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // A page opened before a deploy still asks for the old bundle's chunk
    // hashes, which the new image no longer serves. Reload once to pick up the
    // new bundle; the session flag stops a loop if the chunk is truly missing.
    const msg = String(error?.message || '');
    if (error?.name === 'ChunkLoadError' || /Loading (CSS )?chunk \d+ failed/i.test(msg)) {
      try {
        if (!sessionStorage.getItem('mvkg:chunkReload')) {
          sessionStorage.setItem('mvkg:chunkReload', '1');
          window.location.reload();
          return;
        }
      } catch (_) { /* storage blocked: fall through to the fallback UI */ }
    }
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  componentDidMount() {
    // Still standing a while after load: allow the next deploy's one-time
    // reload again. Cleared late on purpose — cleared at mount, a chunk that is
    // really gone would reload forever.
    this.clearTimer = window.setTimeout(() => {
      try { sessionStorage.removeItem('mvkg:chunkReload'); } catch (_) { /* ignore */ }
    }, 15000);
  }

  componentWillUnmount() {
    window.clearTimeout(this.clearTimer);
  }

  componentDidUpdate(prevProps) {
    // Clear the error when the route changes so navigating away recovers.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          textAlign: 'center',
        }}
      >
        <Stack spacing={2} alignItems="center" sx={{ maxWidth: 420 }}>
          <WarnIcon sx={{ fontSize: 40, color: 'warning.main' }} />
          <Typography variant="h6" fontWeight={700}>This view hit an error</Typography>
          <Typography variant="body2" color="text.secondary">
            Something went wrong rendering this page. Your data is safe — switch to
            another tab, or reload.
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ wordBreak: 'break-word' }}>
            {String(this.state.error?.message || this.state.error).slice(0, 200)}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={() => this.setState({ error: null })}>
              Try again
            </Button>
            <Button variant="outlined" onClick={() => { window.location.href = '/'; }}>
              Go home
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }
}
